import React from 'react';
import { Box, Button, CircularProgress, LinearProgress, Typography } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface SubmitBarProps {
  mode: 'upsert' | 'delete';
  totalCount: number;
  newCount: number;
  existingCount: number;
  existingAction: 'skip' | 'update';
  onSubmit: () => void;
  onReset: () => void;
  loading: boolean;
  batchProgress: { current: number; total: number } | null;
  disabled: boolean;
  isPreviewAction?: boolean;
}

const SubmitBar: React.FC<SubmitBarProps> = ({
  mode,
  totalCount,
  newCount,
  existingCount,
  existingAction,
  onSubmit,
  onReset,
  loading,
  batchProgress,
  disabled,
  isPreviewAction = false,
}) => {
  const getButtonLabel = () => {
    if (isPreviewAction) {
      if (mode === 'delete') {
        return `Buscar ${totalCount} organizacion${totalCount !== 1 ? 'es' : ''} para eliminar`;
      }
      return `Generar vista previa (${totalCount} registro${totalCount !== 1 ? 's' : ''})`;
    }

    if (mode === 'delete') {
      return `Eliminar ${totalCount} organizacion${totalCount !== 1 ? 'es' : ''}`;
    }

    if (existingAction === 'update') {
      const parts: string[] = [];
      if (newCount > 0) parts.push(`Crear ${newCount}`);
      if (existingCount > 0) parts.push(`Actualizar ${existingCount}`);
      return parts.length > 0 ? parts.join(' + ') : 'Enviar';
    }

    return `Crear ${newCount} organizacion${newCount !== 1 ? 'es' : ''}${existingCount > 0 ? ` (omitir ${existingCount})` : ''}`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {loading && batchProgress && (
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="textSecondary">
              Procesando lote {batchProgress.current} de {batchProgress.total}...
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {Math.round((batchProgress.current / batchProgress.total) * 100)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(batchProgress.current / batchProgress.total) * 100}
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onReset}
          disabled={loading}
        >
          Subir nuevo archivo
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {loading && !batchProgress && <CircularProgress size={20} />}
          <Button
            variant={isPreviewAction ? 'outlined' : 'contained'}
            color={mode === 'delete' ? 'error' : 'primary'}
            onClick={onSubmit}
            disabled={disabled || loading}
            startIcon={loading ? null : isPreviewAction ? <VisibilityIcon /> : mode === 'delete' ? <DeleteIcon /> : <UploadIcon />}
          >
            {getButtonLabel()}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SubmitBar;
